import { RefObject, useEffect, useRef, useState } from "react";

interface ClickOutside {
  clickOutsideRef: RefObject<HTMLElement | null>;
  isClickOutside: boolean;
  toggleOutsideClick: () => void;
}

/**
 * A React custom hook that handles click events outside a specific DOM element.
 *
 * Returns a ref, toggle state and a function to handle toggle state.
 *
 * @usage
 * Put the ref on the element.
 * Toggle state to change the display from none to block or vice-versa.
 * Toggle handler on the element to trigger the toggle.
 *
 */
const useClickOutside = (type: "single" | "multiple" = "single"): ClickOutside => {
  const clickOutsideRef = useRef<HTMLElement | null>(null);
  const [isClickOutside, setToggle] = useState<boolean>(false);

  useEffect(() => {
    if (!clickOutsideRef!.current) return;

    const onOutsideClick = (e: MouseEvent) => {
      const ref = clickOutsideRef.current;
      if (ref && !ref.contains(e.target as Node)) {
        setToggle(false);
      }
    };

    if (isClickOutside) {
      window.addEventListener("click", onOutsideClick);
    } else {
      window.removeEventListener("click", onOutsideClick);
    }

    return () => {
      window.removeEventListener("click", onOutsideClick);
    };
  }, [isClickOutside, clickOutsideRef]);

  function toggleOutsideClick() {
    if (type === "multiple") {
      setToggle(true);
    } else {
      setToggle((prev) => !prev);
    }
  }

  return { clickOutsideRef, isClickOutside, toggleOutsideClick } as const;
};

export default useClickOutside;
